// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React, { useMemo } from 'react';
import LiveChartCard from './LiveChartCard';
import PhaseBandLegend from '../../../components/charts/PhaseBandLegend';
import type { LiveChartCardProps } from './LiveChartCard';
import {
  buildAmbientTempSpec,
  buildCoolingLoadSpec,
  buildHvacLoadKwSpec,
  buildItLoadSpec,
  buildSetpointSpec,
  buildStagingSpec,
  buildTariffSpec,
  buildTotalGridLoadSpec,
  baselineOnly,
  withLiveSeries,
  withLiveSeriesOrAlone,
} from '../../../utils/livePlanSeries';
import {
  axisFractionAt,
  buildHourTicks,
  buildPhaseBandsOnAxis,
  formatAxisClock,
} from '../../../utils/planSeries';
import { useSiteTime } from '../../../hooks/useSiteTime';
import { describeCoverage } from '../../../hooks/useLivePlanTelemetry';
import type { UseChillerPlantResult } from '../../../hooks/useChillerPlant';
import type { UseHvacLoadResult } from '../../../hooks/useHvacLoad';
import type { LiveSeriesCoverage } from '../../../hooks/useLivePlanTelemetry';
import type { PlanFile, TimeAxis } from '../../../types';
import type { PlanChartSpec, SeriesInterpolation } from '../../../utils/planSeries';

/** Forecast and recommended curves read blue; live reads green, baseline dashed. */
const ACCENT = '#4589ff';

/** Fallback note for a card whose measured call has not answered yet. */
const NO_EQUIPMENT_TELEMETRY =
  'The plan’s intended curve only — no measured readings have landed for this ' +
  'window yet, so there is nothing to compare it against.';

/**
 * One quantity on one axis: electrical kW, forecast against measured over the
 * same device set. Deliberately not `grid − IT` as a proxy for the unmetered
 * remainder — that is what the optimizer's KPI corresponds to, but the incoming
 * grid total is not trusted.
 */
const ALL_HVAC_ELECTRICAL =
  'Electrical power in kW, forecast against measured, both covering the whole ' +
  'HVAC set: chillers, chilled-water pumps and CRAHs, summed per device. The ' +
  'cooling output this bought is the card above, in thermal kW — a different ' +
  'quantity a COP higher, never read off this scale. DDC-cabinet auxiliary power ' +
  'is not metered, so the measured curve here is a small undercount.';

/**
 * Thermal output and electrical draw are stacked on the page, both in kW, so the
 * note must say which this is — the COP-sized gap reads as a fault otherwise.
 */
const THERMAL_COOLING_OUTPUT =
  'Thermal power delivered in kW — what the chillers removed from the water, not ' +
  'the electricity they drew doing it. Only chillers reporting as running ' +
  'contribute, so an idle unit is absent rather than a zero. The HVAC load card ' +
  'below is the electrical side of the same work and sits a COP (~4–6×) lower.';

/**
 * Reconstructed from change-of-value transitions: the point publishes only when
 * it flips, so the last reading carries forward. Drawn as steps because chillers
 * switch rather than ramp, and the reconstruction is exact — the short dwells it
 * reveals are upstream's.
 */
const STAGING_IS_RECONSTRUCTED =
  'Measured count reconstructed from change-of-value transitions, drawn as steps ' +
  'because a stage count is discrete. Each 15-minute point is the stage that ran ' +
  'longest in that interval, not an average. Dwell times of 1–5 minutes are what ' +
  'the feed reports and are not yet confirmed as real equipment behaviour.';

/**
 * What the forecast side of each card is drawn from.
 *
 * - `plan` — an executing plan: baseline, recommended and measured.
 * - `baseline` — no plan approved to run now; today's narration supplies the
 *   baseline only, because the recommended curve is a plan nobody is following.
 * - `measured` — no narration for today at all; measured curves alone.
 */
export type LiveChartMode = 'plan' | 'baseline' | 'measured';

/** {@link NO_EQUIPMENT_TELEMETRY} for a day with no plan running. */
const NO_MEASURED_YET =
  'No measured readings have landed for this window yet — the baseline curve only.';

/** Absent-note copy for the two modes with no plan running. */
const NO_BASELINE = 'No baseline or measured readings for this card today.';

/**
 * A card's absent note when its live read failed outright and no plan is
 * running. A card with no curve hides its footnote, so without this an outage
 * would read as "no data today".
 */
const readFailed = (what: string) => `${what} could not be loaded — the request failed.`;

export interface LiveChartGridProps {
  mode?: LiveChartMode;
  /** The site whose y-axis preferences the kW cards read and write. */
  siteId?: string;
  /** The narration, when there is one; `measured` mode draws without it. */
  planFile: PlanFile | null;
  /** The axis every series is drawn on — the plan's, or the site's day. */
  timeAxis: TimeAxis;
  itLoad: LiveSeriesCoverage;
  grid: LiveSeriesCoverage;
  /** Measured outside-air temperature, aligned to the plan axis. */
  ambientObserved: (number | null)[];
  /** The weather request failed with nothing cached. */
  ambientFailed?: boolean;
  /** Measured HVAC electrical load — chillers, pumps and CRAHs — in one call. */
  hvacLoad: UseHvacLoadResult;
  /** Cooling load, staging and supply temperature, from one composed call. */
  chillerPlant: UseChillerPlantResult;
  now: Date;
}

/** A card plus where it belongs; an unresolved spec sinks to the bottom band. */
type Card = Omit<LiveChartCardProps, 'accent' | 'bands' | 'hourTicks' | 'cursor' | 'siteId'> & {
  key: string;
};

/**
 * Whether a spec ended up with a measured curve. `withLiveSeries` drops an
 * all-null series rather than adding an empty legend entry, so its absence is
 * the signal that nothing has landed for this window yet.
 */
function hasMeasured(spec: PlanChartSpec | undefined): boolean {
  return Boolean(spec?.series.some(s => s.variant === 'live'));
}

export default function LiveChartGrid({
  mode = 'plan',
  siteId,
  planFile,
  timeAxis,
  itLoad,
  grid,
  ambientObserved,
  ambientFailed = false,
  hvacLoad,
  chillerPlant,
  now,
}: LiveChartGridProps) {
  const rawInputs = mode === 'measured' ? undefined : planFile?.raw_inputs;
  const { siteZone } = useSiteTime();
  const phases = mode === 'plan' ? planFile?.phases : undefined;

  const bands = useMemo(
    () => (phases ? buildPhaseBandsOnAxis(phases, timeAxis) : []),
    [phases, timeAxis]
  );

  /**
   * One card's spec in the current mode. With a plan running this is exactly
   * `withLiveSeries`; otherwise the recommended curve comes out and a card with
   * no forecast still charts its measured curve on its own.
   */
  const compose = useMemo(
    () =>
      (
        forecast: PlanChartSpec | undefined,
        values: (number | null)[] | undefined,
        label: string,
        unit: string,
        options: { interpolation?: SeriesInterpolation } = {}
      ) =>
        mode === 'plan'
          ? withLiveSeries(forecast, values, label, 'live', options)
          : withLiveSeriesOrAlone(baselineOnly(forecast), values, unit, label, options),
    [mode]
  );
  const hourTicks = useMemo(
    () => (timeAxis ? buildHourTicks(timeAxis, 3, siteZone) : []),
    [timeAxis, siteZone]
  );
  // Indexed by timestep, which is what the chart hands back on hover.
  const hoverLabelAt = useMemo(
    () => (timeAxis ? (index: number) => formatAxisClock(timeAxis, index, siteZone) : undefined),
    [timeAxis, siteZone]
  );
  const cursor = useMemo(() => {
    const fraction = timeAxis ? axisFractionAt(timeAxis, now) : undefined;
    return fraction === undefined ? undefined : { fraction, label: 'NOW' };
  }, [timeAxis, now]);

  const tariff = useMemo(() => buildTariffSpec(rawInputs), [rawInputs]);
  const itLoadSpec = useMemo(
    () => compose(buildItLoadSpec(rawInputs), itLoad.values, 'Live', 'kW'),
    [compose, rawInputs, itLoad.values]
  );
  const setpoint = useMemo(
    () => compose(buildSetpointSpec(rawInputs), chillerPlant.supplyTemp, 'Measured', '°F'),
    [compose, rawInputs, chillerPlant.supplyTemp]
  );
  const coolingLoad = useMemo(
    () =>
      compose(
        buildCoolingLoadSpec(rawInputs),
        chillerPlant.coolingLoad,
        'Measured (running chillers)',
        'kW'
      ),
    [compose, rawInputs, chillerPlant.coolingLoad]
  );
  const staging = useMemo(
    () =>
      compose(buildStagingSpec(rawInputs), chillerPlant.activeChillerCount, 'Measured', '', {
        interpolation: 'step',
      }),
    [compose, rawInputs, chillerPlant.activeChillerCount]
  );
  const ambient = useMemo(
    () => compose(buildAmbientTempSpec(rawInputs), ambientObserved, 'Observed', '°F'),
    [compose, rawInputs, ambientObserved]
  );
  const hvacSpec = useMemo(
    () =>
      compose(buildHvacLoadKwSpec(rawInputs), hvacLoad.electricPower, 'Measured (all HVAC)', 'kW'),
    [compose, rawInputs, hvacLoad.electricPower]
  );
  const gridSpec = useMemo(
    () => compose(buildTotalGridLoadSpec(rawInputs), grid.values, 'Live', 'kW'),
    [compose, rawInputs, grid.values]
  );

  const shared = { accent: ACCENT, bands, hourTicks, hoverLabelAt, cursor, siteId };

  const gatedFallback = chillerPlant.data?.coverage?.reasons?.gatedFallback;

  /** The plan's own words for why it left IT load alone, when it has any. */
  const planned = mode === 'plan';
  /**
   * A card's absent note: the plan's reason with a plan running, else the day's.
   * With no plan running, a failed read says so instead. With a plan running the
   * card has no curve only because the plan carries none, whatever the read did,
   * so the plan's own reason stays: an outage must not be blamed for that gap.
   */
  const absent = (planNote: string, failedWhat?: string | false) =>
    planned ? planNote : failedWhat ? readFailed(failedWhat) : NO_BASELINE;
  const noTelemetry = planned ? NO_EQUIPMENT_TELEMETRY : NO_MEASURED_YET;

  const itLoadAbsentNote =
    planFile?.actions?.not_requested?.find(change => change.parameter?.startsWith('it_load'))
      ?.note ??
    'This plan carries no IT load forecast — IT load was neither a driver nor a ' +
      'demand-response lever here.';

  const drivers: Card[] = [
    {
      key: 'ambient',
      title: 'Ambient temp',
      spec: ambient,
      footnote: hasMeasured(ambient)
        ? undefined
        : ambientFailed
          ? 'Weather observations could not be loaded — forecast only.'
          : 'No observations have landed for this window yet — forecast only.',
      absentNote: absent(
        'The plan carries no ambient temperature forecast.',
        ambientFailed && 'Weather observations'
      ),
    },
    {
      key: 'it-load',
      title: 'IT load',
      spec: itLoadSpec,
      footnote:
        describeCoverage(itLoad) ?? (planned ? planFile?.drivers?.demand_response_note : undefined),
      absentNote: absent(itLoadAbsentNote, itLoad.requestFailed && 'Live IT load readings'),
    },
    {
      key: 'tariff',
      title: 'Tariff',
      spec: tariff,
      absentNote: planned
        ? `Not driving this optimization. ${
            planFile?.drivers?.tariff?.note ??
            'This plan was solved without tariff data, so there is no price curve to show.'
          }`
        : 'No tariff curve today — the price series comes from the optimizer, and no plan is running.',
    },
  ];

  const ordered = [...drivers.filter(card => card.spec), ...drivers.filter(card => !card.spec)];

  return (
    <div className="live-grid">
      <PhaseBandLegend bands={bands} className="live-band-legend" />

      <section className="live-section" aria-labelledby="live-now-heading">
        <div className="live-section-head">
          <h3 className="live-section-title" id="live-now-heading">
            What&apos;s happening now
          </h3>
        </div>
        {ordered.map(({ key, ...card }) => (
          <LiveChartCard key={key} {...shared} {...card} />
        ))}
      </section>

      <section className="live-section" aria-labelledby="live-outcome-heading">
        <div className="live-section-head">
          <h3 className="live-section-title" id="live-outcome-heading">
            Outcome
          </h3>
        </div>
        <LiveChartCard
          {...shared}
          title="Cooling load (thermal)"
          spec={coolingLoad}
          loading={chillerPlant.coverage.loading}
          liveLoading={chillerPlant.coverage.loading}
          footnote={
            describeCoverage(chillerPlant.coverage) ??
            (typeof gatedFallback === 'string' ? gatedFallback : undefined) ??
            (hasMeasured(coolingLoad) ? THERMAL_COOLING_OUTPUT : noTelemetry)
          }
          absentNote={absent(
            'This plan publishes no cooling load series.',
            chillerPlant.coverage.requestFailed && 'Live cooling load readings'
          )}
        />
        <LiveChartCard
          {...shared}
          title="HVAC load"
          spec={hvacSpec}
          loading={hvacLoad.coverage.loading}
          liveLoading={hvacLoad.coverage.loading}
          footnote={
            describeCoverage(hvacLoad.coverage) ??
            (hasMeasured(hvacSpec) ? ALL_HVAC_ELECTRICAL : noTelemetry)
          }
          absentNote={absent(
            'This plan publishes no HVAC power series.',
            hvacLoad.coverage.requestFailed && 'Live HVAC load readings'
          )}
        />
        <LiveChartCard
          {...shared}
          title="Total grid load"
          spec={gridSpec}
          loading={grid.loading}
          liveLoading={grid.loading}
          footnote={describeCoverage(grid) ?? undefined}
          absentNote={absent(
            'This plan publishes no grid load series.',
            grid.requestFailed && 'Live grid load readings'
          )}
        />
      </section>

      <section className="live-section live-section--wide" aria-labelledby="live-equipment-heading">
        <div className="live-section-head">
          <h3 className="live-section-title" id="live-equipment-heading">
            Equipment status
          </h3>
        </div>
        <LiveChartCard
          {...shared}
          title="Chiller supply setpoint"
          spec={setpoint}
          loading={chillerPlant.coverage.loading}
          liveLoading={chillerPlant.coverage.loading}
          footnote={
            describeCoverage(chillerPlant.coverage) ??
            (hasMeasured(setpoint) ? undefined : noTelemetry)
          }
          absentNote={absent(
            'No chilled-water setpoint series in this plan.',
            chillerPlant.coverage.requestFailed && 'Live chiller setpoint readings'
          )}
        />
        <LiveChartCard
          {...shared}
          title="Chiller staging"
          spec={staging}
          loading={chillerPlant.coverage.loading}
          liveLoading={chillerPlant.coverage.loading}
          footnote={
            describeCoverage(chillerPlant.coverage) ??
            (hasMeasured(staging) ? STAGING_IS_RECONSTRUCTED : noTelemetry)
          }
          absentNote={absent(
            'No chiller staging series in this plan.',
            chillerPlant.coverage.requestFailed && 'Live chiller staging readings'
          )}
        />
      </section>
    </div>
  );
}
